/* eslint-disable unicorn/prefer-module */
import { useMemo, useState } from "react";
import { ImCheckboxChecked, ImCheckboxUnchecked } from "react-icons/im";
import cx from "classix";
import { BadgeDato, CategoriaLeyendaIpsos, Loader } from "..";
import { supuesto } from "../../data/ipsos2025";
import { IPSOS_BELLEZA, MONTOS_FIJOS } from "../../data/ipsos2025/belleza";
import { useCategoriaMoneda, useUniversoCategorias } from "../../utils";
import { Header } from "../Header";
import styles from "./CategoriaBelleza.module.scss";

// Promedio ponderado de gasto (midpoints × share), redondeado a la centena → $39.800 —
// supuesto comercial derivado de los tramos Ipsos (gastoMensual)
const PROMEDIO_PONDERADO_GASTO_VALOR =
  Math.round(IPSOS_BELLEZA.gastoMensual.reduce((s, g) => s + g.pct.valor * g.midpoint.valor, 0) / 100) * 100;
const PROMEDIO_PONDERADO_GASTO = supuesto(PROMEDIO_PONDERADO_GASTO_VALOR, {
  nota: "promedio ponderado de gasto (Σ pct Ipsos × punto medio del tramo)",
});

// Checkpoints de monto (gasto mensual por persona)
const MONTOS = [
  ...MONTOS_FIJOS.map((m) => ({ valor: m.valor, promedio: false })),
  { valor: PROMEDIO_PONDERADO_GASTO.valor, promedio: true },
];

// Marcas ordenadas por share desc (para el grid de market share)
const MARCAS_ESPONTANEA = [...IPSOS_BELLEZA.marcasEspontanea].sort((a, b) => b.pct.valor - a.pct.valor);
const MARCAS_INDUCIDA = [...IPSOS_BELLEZA.marcasGuiada].sort((a, b) => b.pct.valor - a.pct.valor);

const pct0 = (pct) => `${(pct * 100).toFixed(0)}%`;

export const Belleza = () => {
  const { formatNumber, formatLocal, convertCLP, pais } = useCategoriaMoneda();
  const {
    edificios,
    loading,
    error,
    checkAll,
    checkedBuildings,
    toggleCheck,
    toggleCheckAll,
    cantidadEdificios,
    totalPantallas,
    totalEjecutivos,
    totalEmpresas,
  } = useUniversoCategorias(pais);
  const [showInventarioModal, setShowInventarioModal] = useState(false);
  const [verAnual, setVerAnual] = useState(false);
  const [montoSeleccionado, setMontoSeleccionado] = useState(PROMEDIO_PONDERADO_GASTO.valor);

  // =====================================================
  // CÁLCULOS PRINCIPALES — recalcula con el inventario y el monto
  // =====================================================
  const resultados = useMemo(() => {
    if (!totalEjecutivos) return null;

    const compradores = Math.round(totalEjecutivos * IPSOS_BELLEZA.penetracion.usa.valor);
    const noUsan = Math.round(totalEjecutivos * IPSOS_BELLEZA.penetracion.noUsa.valor);

    const desgloseFrecuencia = IPSOS_BELLEZA.frecuencia.map((f) => {
      const personas = Math.round(compradores * f.pct.valor);
      const tickets = Math.round(personas * f.factor.valor);
      return { label: f.label, pct: f.pct.valor, pctDato: f.pct, personas, tickets };
    });
    const ticketsMes = desgloseFrecuencia.reduce((s, d) => s + d.tickets, 0);
    const mercadoMensual = compradores * montoSeleccionado;

    const marketShare = (marcas) =>
      marcas.map((m) => ({
        nombre: m.nombre,
        dato: m.pct,
        share: m.pct.valor,
        clientes: Math.round(compradores * m.pct.valor),
        mercadoMensual: Math.round(compradores * m.pct.valor * montoSeleccionado),
      }));

    return {
      compradores,
      noUsan,
      desgloseFrecuencia,
      ticketsMes,
      ticketsAnio: ticketsMes * 12,
      mercadoMensual,
      mercadoAnual: mercadoMensual * 12,
      marketEspontanea: marketShare(MARCAS_ESPONTANEA),
      marketInducida: marketShare(MARCAS_INDUCIDA),
    };
  }, [totalEjecutivos, montoSeleccionado]);

  if (loading) return <Loader />;
  if (error) return <p className={styles.disclaimer}>{error}</p>;

  const tipoProductoEntries = Object.values(IPSOS_BELLEZA.tipoProducto);

  // Ranking de barras horizontales (tipo de producto / dónde compra)
  const renderBarras = (titulo, entries) => (
    <div className={styles.appsCard}>
      <h4 className={styles.subtitulo}>{titulo}</h4>
      <div className={styles.appsList}>
        {entries.map(({ label, pct }) => (
          <div key={label} className={styles.appItem}>
            <div className={styles.appNombre}>{label}</div>
            <div className={styles.appBar}>
              <div className={styles.appBarFill} style={{ width: `${pct.valor * 100}%` }} />
            </div>
            <div className={styles.appStats}>
              <BadgeDato dato={pct}>{pct0(pct.valor)}</BadgeDato>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Grid de cards de market share por marca
  const renderMarketShare = (titulo, data) => (
    <div className={styles.marketShareContainer}>
      <h4 className={styles.marketShareTitulo}>{titulo}</h4>
      <div className={styles.marketShareGrid}>
        {data.map((m) => (
          <div key={m.nombre} className={styles.marketShareCard}>
            <div className={styles.cardNombre}>{m.nombre}</div>
            <div className={styles.cardShare}>
              <BadgeDato dato={m.dato}>{pct0(m.share)}</BadgeDato>
            </div>
            <div className={styles.cardMetricas}>
              <div>
                <span className={styles.metricaLabel}>Clientes</span>
                <span className={styles.metricaValor}>{formatNumber(m.clientes)}</span>
              </div>
              <div>
                <span className={styles.metricaLabel}>Mercado/{verAnual ? "año" : "mes"}</span>
                <span className={styles.metricaValor}>
                  {formatLocal(convertCLP(verAnual ? m.mercadoMensual * 12 : m.mercadoMensual))}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className={styles.bellezaPage}>
      <Header leftContent={<h1 className={styles.headerTitulo}>BELLEZA Y CUIDADO PERSONAL</h1>}>
        <div className={styles.inventarioHeader}>
          <div className={styles.inventarioInfo}>
            <span>
              <strong>{cantidadEdificios}</strong> edificios
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalPantallas)}</strong> pantallas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEjecutivos)}</strong> personas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEmpresas)}</strong> empresas
            </span>
          </div>
          <button type="button" className={styles.inventarioBtn} onClick={() => setShowInventarioModal(true)}>
            Inventario
          </button>
        </div>
      </Header>

      <CategoriaLeyendaIpsos />

      <div className={styles.bellezaContent}>
        {/* Tabla maestra: penetración + frecuencia + tickets */}
        {resultados && (
          <div className={styles.tablaContainer}>
            <table className={cx("table table-sm", styles.tablaResultados)}>
              <thead className="table-dark">
                <tr>
                  <th className={cx("text-center", styles.thNoCompran)}>No usan</th>
                  <th className={cx("text-center", styles.thSiCompran)}>Sí usan</th>
                  <th className={cx("text-center", styles.thFrecuencia)}>Frecuencia de compra</th>
                  <th className={cx("text-center", styles.thPersonas)}>Según frecuencia</th>
                  <th className={cx("text-center", styles.thConversiones)}>Tickets {verAnual ? "año" : "mes"}</th>
                </tr>
              </thead>
              <tbody>
                {resultados.desgloseFrecuencia.map((data, idx) => (
                  <tr key={data.label}>
                    {idx === 0 && (
                      <>
                        <td
                          rowSpan={resultados.desgloseFrecuencia.length}
                          className={cx("text-center align-middle", styles.celdaNo)}
                        >
                          <div className={styles.celdaContenido}>
                            <BadgeDato dato={IPSOS_BELLEZA.penetracion.noUsa} className={styles.badgeOportunidad}>
                              {pct0(IPSOS_BELLEZA.penetracion.noUsa.valor)}
                            </BadgeDato>
                            <div className={styles.valorMuyGrande}>{formatNumber(resultados.noUsan)}</div>
                            <div className={styles.subtextoOportunidad}>OPORTUNIDAD</div>
                          </div>
                        </td>
                        <td
                          rowSpan={resultados.desgloseFrecuencia.length}
                          className={cx("text-center align-middle", styles.celdaSi)}
                        >
                          <div className={styles.celdaContenido}>
                            <BadgeDato dato={IPSOS_BELLEZA.penetracion.usa}>
                              {pct0(IPSOS_BELLEZA.penetracion.usa.valor)}
                            </BadgeDato>
                            <div className={styles.valorMuyGrande}>{formatNumber(resultados.compradores)}</div>
                          </div>
                        </td>
                      </>
                    )}
                    <td className={styles.celdaFrecuencia}>
                      <div className={styles.frecuenciaInner}>
                        <BadgeDato dato={data.pctDato}>{pct0(data.pct)}</BadgeDato>
                        <span>{data.label}</span>
                      </div>
                    </td>
                    <td className="text-center">{formatNumber(data.personas)}</td>
                    <td className="text-center">{formatNumber(verAnual ? data.tickets * 12 : data.tickets)}</td>
                  </tr>
                ))}
                <tr className={styles.filaTotales}>
                  <td colSpan={4} className={styles.celdaVacia} />
                  <td className={cx("text-center", styles.celdaTotal)}>
                    <strong>{formatNumber(verAnual ? resultados.ticketsAnio : resultados.ticketsMes)}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Gasto (radios) + Mercado (toggle) */}
        {resultados && (
          <div className={styles.dosBloquesContainer}>
            <div className={styles.bloqueTicket}>
              <div className={styles.ticketLabel}>Gasto mensual por persona (supuesto)</div>
              <div className={styles.ticketBox}>
                <div className={styles.ticketOpciones}>
                  {MONTOS.map((m) => (
                    <label key={m.promedio ? "promedio" : m.valor} className={styles.ticketRadio}>
                      <input
                        type="radio"
                        name="montoBelleza"
                        checked={montoSeleccionado === m.valor}
                        onChange={() => setMontoSeleccionado(m.valor)}
                      />
                      <span>
                        {m.promedio
                          ? `Promedio (${formatLocal(convertCLP(m.valor))})`
                          : formatLocal(convertCLP(m.valor))}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className={styles.bloqueMercado}>
              <div className={styles.mercadoLabelRow}>
                <span className={styles.mercadoLabel}>Mercado {verAnual ? "anual" : "mensual"}</span>
                <div className={styles.toggleContainer}>
                  <span className={cx(styles.toggleLabel, !verAnual && styles.toggleActive)}>mes</span>
                  <label className={styles.toggleSwitch}>
                    <input type="checkbox" checked={verAnual} onChange={(e) => setVerAnual(e.target.checked)} />
                    <span className={styles.toggleSlider} />
                  </label>
                  <span className={cx(styles.toggleLabel, verAnual && styles.toggleActive)}>año</span>
                </div>
              </div>
              <div className={styles.mercadoValorGrande}>
                {formatLocal(convertCLP(verAnual ? resultados.mercadoAnual : resultados.mercadoMensual))}
              </div>
            </div>
          </div>
        )}

        {/* Market Share por Marca — espontánea + inducida */}
        {resultados && renderMarketShare("Market Share por Marca — mención espontánea", resultados.marketEspontanea)}
        {resultados && renderMarketShare("Market Share por Marca — mención inducida", resultados.marketInducida)}

        {/* Cómo se compra — barras */}
        <div className={styles.appsSection}>
          {renderBarras("Tipo de producto que compra", tipoProductoEntries)}
          {renderBarras("Dónde compra", IPSOS_BELLEZA.dondeCompra)}
        </div>

        {/* Factores + Importancia — chips */}
        <div className={styles.valoracionContainer}>
          <h4 className={styles.subtitulo}>Factores que prioriza</h4>
          <div className={styles.valoracionGrid}>
            {IPSOS_BELLEZA.factores.map(({ label, pct }) => (
              <div key={label} className={styles.valoracionItem}>
                <span className={styles.valoracionNombre}>{label}</span>
                <BadgeDato dato={pct}>{pct0(pct.valor)}</BadgeDato>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.valoracionContainer}>
          <h4 className={styles.subtitulo}>Importancia de la categoría</h4>
          <div className={styles.valoracionGrid}>
            {IPSOS_BELLEZA.importancia.map(({ label, pct }) => (
              <div key={label} className={styles.valoracionItem}>
                <span className={styles.valoracionNombre}>{label}</span>
                <BadgeDato dato={pct}>{pct0(pct.valor)}</BadgeDato>
              </div>
            ))}
          </div>
        </div>

        {/* Disclaimer */}
        <div className={styles.disclaimer}>
          <p>
            <strong>Ipsos 2025 (láminas 60-62, base 400/261):</strong> el 66% usa productos de belleza y cuidado
            personal; el 77% considera la categoría muy importante y la calidad (90%) es el principal factor de
            decisión. Los rótulos de canales &quot;Tiendas por departamento&quot;, &quot;Tiendas especializadas&quot; y
            &quot;Plataformas online&quot; fueron definidos internamente (venían truncados en la fuente).
          </p>
          <p>
            <strong>Supuestos comerciales:</strong> tickets/mes por frecuencia, punto medio de los tramos de gasto y el
            promedio ponderado. El mercado se calcula sobre la audiencia seleccionada en el inventario.
          </p>
        </div>
      </div>

      {/* Modal de Inventario */}
      {showInventarioModal && (
        <div className={styles.modalOverlay} onClick={() => setShowInventarioModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Inventario de Edificios</h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowInventarioModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalSummary}>
                <span>
                  <strong>{cantidadEdificios}</strong> edificios seleccionados
                </span>
                <span>
                  <strong>{formatNumber(totalEjecutivos)}</strong> personas
                </span>
                <button type="button" className={styles.toggleAllBtn} onClick={toggleCheckAll}>
                  {checkAll ? "Deseleccionar todos" : "Seleccionar todos"}
                </button>
              </div>
              <table className="table table-sm table-hover">
                <thead className="table-dark">
                  <tr>
                    <th className="text-center">✓</th>
                    <th className="text-center">Edificio</th>
                    <th className="text-center">Ubicación</th>
                    <th className="text-center">Pantallas</th>
                    <th className="text-center">Personas</th>
                  </tr>
                </thead>
                <tbody>
                  {edificios.map(({ id, edificio, ubicacion, personas, pantallas, espera, totems, cowork }) => (
                    <tr
                      key={id}
                      onClick={() => toggleCheck(id)}
                      className={cx(checkedBuildings[id] && "table-success")}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="text-center">
                        {checkedBuildings[id] ? <ImCheckboxChecked size={16} /> : <ImCheckboxUnchecked size={16} />}
                      </td>
                      <td className="text-center">{edificio.toUpperCase()}</td>
                      <td className="text-center">{ubicacion}</td>
                      <td className="text-center">
                        {formatNumber((pantallas || 0) + (espera || 0) + (totems || 0) + (cowork || 0))}
                      </td>
                      <td className="text-center">{personas ? formatNumber(personas) : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalBtn} onClick={() => setShowInventarioModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Belleza;
